import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FfkLogo from "../../assets/images/ffk-logo.png";
import styles from "./NavigationBar.module.scss";
import {
  DownOutlined,
  MenuOutlined,
  SearchOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import i18n from "../../i18n";
import useFetch from "../../hooks/useFetch";
import { CategoriesResponse, SubCategory } from "../../types/types";
import { Link, useNavigate } from "react-router-dom";
import { replaceStringSpaces } from "../../utils/utils";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import SearchBar from "../search-bar/SearchBar";
import { Dropdown, Menu, Modal } from "antd";

const NavigationBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useFetch<CategoriesResponse>("/categories/top-level");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language.toUpperCase());
  const menuRef = useRef<HTMLDivElement>(null);

  const { width } = useWindowDimensions();
  const isMobile = useMemo(() => (width ? width < 768 : false), [width]);

  const handleLanguageChange = useCallback((language: string) => {
    i18n.changeLanguage(language);
    localStorage.setItem("language", language);
    setSelectedLanguage(language.toUpperCase());
  }, []);

  const languageMenu = (
    <Menu onClick={({ key }) => handleLanguageChange(key)}>
      <Menu.Item key="sq">SQ</Menu.Item>
      <Menu.Item key="en">EN</Menu.Item>
    </Menu>
  );

  const handleMenuClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsMenuOpen(true);
  }, []);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target as HTMLElement)
    ) {
      setIsMenuOpen(false);
      setOpenCategory(null);
    }
  }, []);

  const handleOpenCategory = useCallback((categoryName: string) => {
    setOpenCategory((prev) => (prev === categoryName ? null : categoryName));
  }, []);

  const handleFFKCategoryNavigation = useCallback(
    (categoryName: string) => {
      const ffkRoutes: Record<string, string> = {
        Komiteti: "/committee",
        Administrata: "/administration",
        Presidenti: "/presidenti",
        Komisionet: "/committees",
        Sekretari: "/sekretar-i-pergjithshem",
        Normative: "/normative-acts",
        Historiku: "/ffk-historiku",
      };

      const matchedKey = Object.keys(ffkRoutes).find((key) =>
        categoryName.includes(key),
      );

      if (matchedKey) {
        navigate(ffkRoutes[matchedKey]);
      }
    },
    [navigate],
  );

  const handleNavigation = useCallback(
    (category: SubCategory, clickedSubCategory: SubCategory) => {
      const state = { category, subCategory: clickedSubCategory };

      if (openCategory === "GARAT") {
        navigate(`/garat/${replaceStringSpaces(clickedSubCategory.name)}`, {
          state: { categoryId: clickedSubCategory.referenceLeagueId },
        });
        return;
      }

      if (openCategory === "FFK") {
        handleFFKCategoryNavigation(clickedSubCategory.name);
        return;
      }

      if (openCategory === "TË TJERAT") {
        const teTjeraRoutes: Record<string, string> = {
          GRASSROOTS: "/grassroots",
          PLAYMAKERS: "/playmakers",
          "Licencimi i klubeve": "/licencimi-i-klubeve",
          Auditori: "/auditor",
        };

        const route = teTjeraRoutes[clickedSubCategory.name];
        if (route) {
          navigate(route, { state });
        }
        return;
      }

      navigate("/lajmet", { state });
    },
    [openCategory, navigate, handleFFKCategoryNavigation],
  );

  const handleLogoClick = useCallback(() => {
    if (window.location.pathname === "/") {
      window.scrollTo({ top: 0 });
    } else {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (isMenuOpen) {
      html.style.overflow = "hidden";
      body.style.overflow = "hidden";
      body.style.position = "fixed";
      body.style.width = "100%";
    } else {
      html.style.overflow = "";
      body.style.overflow = "";
      body.style.position = "";
      body.style.width = "";
    }

    return () => {
      html.style.overflow = "";
      body.style.overflow = "";
      body.style.position = "";
      body.style.width = "";
    };
  }, [isMenuOpen]);

  const Logo = (
    <div
      className={styles.LogoImage}
      onClick={handleLogoClick}
      style={{ cursor: "pointer" }}
    >
      <img src={FfkLogo} alt="ffk-logo" />
    </div>
  );

  const MenuItems = (
    <div className={styles.NavigationLinksMenu}>
      {data?.map((item) => (
        <h4 key={item.name} onClick={() => handleOpenCategory(item.name)}>
          {item.name}
          {openCategory === item.name ? <UpOutlined /> : <DownOutlined />}
        </h4>
      ))}
    </div>
  );

  return (
    <div className={styles.NavigationBar}>
      {!isMobile && Logo}

      <div className={styles.NavigationBarAlignment}>
        <div className={styles.NavigationBarM}>
          <div className={styles.NavigationBarMain}>
            {/* Top navigation links */}
            <div className={styles.NavigationBarItems}>
              <a
                href="https://eu.ebileta.al/biglietteria/listaEventiPub.do"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("app.navigation.biletaria", "Biletaria")}
              </a>
              <Link to="ffk-shop">
                {t("app.navigation.ffkshop", "FFK Shop")}
              </Link>
              <a
                href="https://ffk-media.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("app.navigation.akreditimi", "Akreditimi")}
              </a>
              <a
                href="https://ffk-ks.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("app.navigation.arkiva", "Arkiva")}
              </a>
            </div>

            <div className={styles.NavigationBarLanguages}>
              <a
                className={styles.MobileNavLink}
                href="https://eu.ebileta.al/biglietteria/listaEventiPub.do"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("app.navigation.biletaria", "Biletaria")}
              </a>
              <Link className={styles.UtilityLink} to="/fan-club">
                {t("app.fanClub.title", "Dardanët")}
              </Link>
              <Link className={styles.UtilityLink} to="/volunteers">
                <span className={styles.DesktopLinkLabel}>
                  {t("app.volunteers.program", "Programi i Vullnetarëve")}
                </span>
                <span className={styles.MobileLinkLabel}>
                  {t("app.volunteers.program.mobile", "Vullnetarët")}
                </span>
              </Link>
              <Link className={styles.UtilityLink} to="/coaches">
                <span className={styles.DesktopLinkLabel}>
                  {t("app.coaches.program", "Aplikimi për Trajner")}
                </span>
                <span className={styles.MobileLinkLabel}>
                  {t("app.coaches.program.mobile", "Trajnerët")}
                </span>
              </Link>

              <div className={styles.LanguagesMobile}>
                <Dropdown overlay={languageMenu}>
                  <a onClick={(e) => e.preventDefault()}>
                    {selectedLanguage}
                    <DownOutlined />
                  </a>
                </Dropdown>
              </div>
            </div>
          </div>

          <div className={styles.NavigationBarSearch}>
            <SearchOutlined
              onClick={() => setIsSearchOpen(true)}
              style={{ cursor: "pointer" }}
            />
            <Modal
              open={isSearchOpen}
              onCancel={() => setIsSearchOpen(false)}
              footer={null}
            >
              <SearchBar closeModal={() => setIsSearchOpen(false)} />
            </Modal>
          </div>
        </div>

        <div className={styles.LogoMenuRow}>
          {isMobile && !isMenuOpen && Logo}
          <div
            ref={menuRef}
            className={classNames(styles.NavigationBarMenu, {
              [styles.NavigationBarMenuOpen]: isMenuOpen,
            })}
            onClick={handleMenuClick}
          >
            {!isMenuOpen ? (
              <>
                <MenuOutlined style={{ fontSize: "15px" }} />
                {!isMobile && <p>{t("app.navigation.menu", "Menu")}</p>}
              </>
            ) : (
              MenuItems
            )}
          </div>
        </div>

        {openCategory !== null && (
          <div
            className={classNames(styles.NavigationLinksMenuItem, {
              [styles.GaratMenu]:
                openCategory === "GARAT" || openCategory === "FFK",
            })}
          >
            {data
              ?.find((item) => item.name === openCategory)
              ?.subCategories.map((subCat) => (
                <div
                  key={subCat.name}
                  className={styles.NavigationLinksMenuItemSubCat}
                >
                  <h4>{subCat.name}</h4>
                  {subCat.subCategories.map((cat) => (
                    <h5
                      key={cat.name}
                      onClick={() => handleNavigation(subCat, cat)}
                    >
                      {cat.name}
                    </h5>
                  ))}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NavigationBar;
